import React, { memo, forwardRef, useImperativeHandle } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Slider from '@material-ui/core/Slider'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import { useConstructor, useBaseComponentOperations } from '../../hooks'
import { fontSizeBodyKey } from '../../themes'
import { defaultColor } from './common'
import { translate } from '../../multilanguage'

const defaultWidth = '200px'

let sliderId = 0

const BaseSliderContainer = styled.div`
  width: ${props => props.width || defaultWidth};
`

/**
 * Implements Mui Slider.
 *
 * @param {object} props component props
 * @returns {BaseSlider} BaseSlider component
 */
const BaseSlider = memo(forwardRef((props, ref) => {
  useConstructor(() => ++sliderId)
  const idtemplate = `BaseSlider-label${sliderId}`
  const [value, onChange, setValue] = useBaseComponentOperations(props, 'value', props.defaultValue !== undefined ? props.defaultValue : 0, true)
  useImperativeHandle(ref, () => ({
    getValue: () => value,
    setValue: value => setValue(value)
  }))
  const {
    rightIcon,
    leftIcon,
    noTranslate,
    ...finalProps
  } = props
  return (
    <BaseSliderContainer>
      {props.label && 
        <Typography variant={fontSizeBodyKey} id={idtemplate} gutterBottom>
          {typeof props.label !== 'string' ? props.label : noTranslate ? props.label : translate(props.label)}
        </Typography>}
      <Grid container spacing={2}>
        {props.leftIcon && (
          <Grid item>
            <props.leftIcon />
          </Grid>
        )}
        <Grid item xs>
          <Slider
            valueLabelDisplay='auto'
            {...finalProps}
            value={value}
            onChange={onChange}
            aria-labelledby={idtemplate}
          />
        </Grid>
        {props.rightIcon && (
          <Grid item>
            <props.rightIcon />
          </Grid>)}
      </Grid>
    </BaseSliderContainer>
  )
}))

BaseSlider.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Initial value for the control.
   */
  defaultValue: PropTypes.number,
  /**
   Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
   onChange function called when the Slider is clicked or moved.
   */
  onChange: PropTypes.func,
  /**
   Icon for the left part of the slider.
   */
  leftIcon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.element,
    PropTypes.node,
    PropTypes.func
  ]),
  /**
   Icon for the right part of the slider.
   */
  rightIcon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.element,
    PropTypes.node,
    PropTypes.func
  ]),
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   MUI color for the control.
   */
  color: PropTypes.oneOf(['primary', 'secondary']),
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
}

BaseSlider.defaultProps = {
  noTranslate: false,
  color: defaultColor
}

export default BaseSlider
